import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router";

import { Storage } from '@ionic/storage';
import { Token, User, AuthenticationModule } from "../interfaces/authInterfaces.ds";

import useAuthenticationModule from "./authenticationModule";
import useRosterModule from "./RosterModule";
import { RosterModule } from "../interfaces/rosterinterfaces.ds";
import useTimesheetEntryModule from "./TimesheetEntryModule";
import { TimesheetEntryModule } from "../interfaces/timesheetentryinterfaces.ds";
// const portalAPI = 'https://markwell.taro.training/api';
const STORE = process.env.REACT_APP_STORE;

export interface Api {
  authentication: AuthenticationModule;
  rosters: RosterModule;
  timesheetEntries: TimesheetEntryModule;
  online: boolean;
  loading: boolean;
  logOut: () => Promise<false | undefined>;
  isportalAuth: boolean;
  clearPortalAPI: () => void;
  portalAPI: string;
  submitPortalAPI: (portal: any) => Promise<boolean | undefined>;
  portalList: string[],
  timeEntryLimit: number,
  setisPortalAuth: any;
  // Why a link from the portal couldn't sign in (shown on the sign-in page).
  launchError: string;
}

/**
 * A portal's API address from a link (?api=https://abc.taro.solutions/api).
 * Only Taro portals; localhost too when running the app locally.
 */
export const allowedPortalApi = (value: string | null): string | null => {
  if (!value) return null;
  const api = value.trim().replace(/\/+$/, '');
  if (/^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.taro\.solutions\/api$/i.test(api)) return api;
  if (process.env.NODE_ENV === 'development' && /^http:\/\/localhost(:\d+)?\/api$/i.test(api)) return api;
  return null;
};

/** "https://abc.taro.solutions/api" -> "abc" (the name kept in the portal list). */
const portalName = (api: string) => api.replace(/^https?:\/\//i, '').replace(/\.taro\.solutions\/api$/i, '').replace(/\/api$/i, '');

export const useServerConnection = () => {
  const history = useHistory();

  const [online, setOnline] = useState<boolean>(true);
  //Used to show loading indicator - stops double submit
  const [loading, setLoading] = useState<boolean>(false);
  //Agree to TCs
  const [isportalAuth, setisPortalAuth] = useState(false);
  const [portalAPI, setPortalAPI] = useState('');
  const [portalList, setPortalList] = useState<string[]>([]);
  const [timeEntryLimit, setTimeEntryLimit] = useState<number>(1);
  const [launchError, setLaunchError] = useState<string>('');
  // A link from the portal (read once, before anything is saved over it).
  const [launchLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return { api: allowedPortalApi(params.get('api')), launch: hash.get('launch') || '' };
  });
  //token
  const [token, setToken] = useState<Token | undefined>(undefined);

  //Setup offline storage
  const store = new Storage();
  store.create();
  //Modules
  //online/offline listener
  useEffect(() => {
    window.addEventListener("offline", () => {
      setOnline(false);
    });
    window.addEventListener("online", () => {
      setOnline(true);
    });

    return () => {

      window.removeEventListener("offline", () => {
        setOnline(false);
      });
      window.removeEventListener("online", () => {
        setOnline(true);
      });
    };
  }, []);

  //On first load
  useEffect(() => {
    console.log('API useEffect');
    (async function getStorage() {
      //Load all data from local storage
      console.log('Load from local storage');
      // Opened from a portal (its tile or a roster email): use that portal.
      if (launchLink.api) {
        // Clear the link (and its pass) from the address bar and history.
        window.history.replaceState(null, '', window.location.pathname);
        await store.set(`${STORE}api`, launchLink.api);
        setPortalAPI(launchLink.api);
        setisPortalAuth(true);
        const name = portalName(launchLink.api);
        setPortalList((list) => list.includes(name) ? list : [...list, name]);
        return;
      }
      const tmpPortalAPI = await store.get(`${STORE}api`);
      if (tmpPortalAPI) {
        setisPortalAuth(true);
        setPortalAPI(tmpPortalAPI);
      }
    })();
  }, []);

  useEffect(() => {
    console.log('API useEffect');
    (async function getStore() {
      //Load all data from local storage
      const tmpPortalAPIList = await store.get(`${STORE}apilist`);
      console.log(tmpPortalAPIList);

      if (tmpPortalAPIList) {
        let temp = JSON.parse(tmpPortalAPIList);
        // Keep any portal just added from a link.
        setPortalList((list) => Array.from(new Set([...temp, ...list])));
      }
    })();
  }, []);

  useEffect(() => {
    (async function saveStorage() {
      if (portalList.length > 0) {
        console.log("list", portalList);

        store.set(`${STORE}apilist`, JSON.stringify(portalList));

        console.log(await store.get(`${STORE}apilist`));
      }
    })();
  }, [portalList]);

  const authentication = useAuthenticationModule(portalAPI, store, setLoading, online, token, setToken, setTimeEntryLimit);

  // The portal's Taro Time tile: sign straight in with its one-time pass
  // (once: the pass only works once).
  const launchSent = useRef(false);
  useEffect(() => {
    if (!launchLink.api || !launchLink.launch || launchSent.current) return;
    launchSent.current = true;
    (async function launch() {
      const ok = await authentication.signInWithPass(launchLink.api as string, launchLink.launch);
      if (!ok) {
        setLaunchError('That link has expired or was already used. Open Taro Time again from your Taro portal, or sign in below with a code.');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rosters = useRosterModule(portalAPI, store, token, setLoading, authentication?.user?.id);

  const timesheetEntries = useTimesheetEntryModule(portalAPI, store, token, setLoading, authentication?.user?.id, rosters);

  const clearPortalAPI = () => {
    console.log('clearAgree');
    store.set(`${STORE}api`, portalAPI);
    setisPortalAuth(true);
  };

  const submitPortalAPI = async (portal: string) => {
    let portalAPI = `https://${portal}.taro.solutions/api`;   //for local: daktech.dev || for live: taro.solutions
    try {
      const response = await fetch(`${portalAPI}/auth.json`, {
        method: 'GET'
      });
      const result = await response.json();
      if (result.message === 'Hello') {
        setPortalAPI(portalAPI);
        store.set(`${STORE}api`, portalAPI);
        setisPortalAuth(true);
        if (!portalList.includes(portal)) {
          setPortalList([...portalList, portal]);
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  }



  const logOut = async () => {
    if (!token) return false;

    setLoading(true);
    try {
      // store.clear();
      await store.remove(`${STORE}api`);
      await store.remove(`${STORE}token`);
      await store.remove(`${STORE}email_token`);
      await store.remove(`${STORE}rosters`);
      await store.remove(`${STORE}user`);
      setLoading(false);
      window.location.reload();
    } catch (err) {

    }
  }

  const api: Api = {
    authentication,
    rosters,
    timesheetEntries,
    online,
    loading,
    isportalAuth,
    portalAPI,
    logOut,
    clearPortalAPI,
    submitPortalAPI,
    portalList,
    timeEntryLimit,
    setisPortalAuth,
    launchError,
  };

  return api;

};

export default useServerConnection;
